"use client";

import initTracker from "@cappelendamm/tracking";
import { getCookie } from "@cappelendamm/cookie";
import { COOKIE_INTERNAL_USER_OR_BOT } from "@/routing";
import Logger from "@cappelendamm/logger";
import { isProduction } from "@/is-production";
import { isStatisticsConsentGranted } from "@/legal/cookie-consent/useCookiebotConsent";
import { TrackingEvent } from "./types";
import { capturePostHogEvent, isCaptureAllowed } from "./posthog/config";

/* window.dataLayer typing can go as far as this example: https://farrant.me/posts/typing-functions-which-might-not-exist */
declare global {
  interface Window {
    dataLayer?: object[];
    [key: string]: any;
  }
}

export function skipTracking(): boolean {
  const isInternal = getCookie(COOKIE_INTERNAL_USER_OR_BOT);
  return Boolean(isInternal) && isProduction;
}

const tracker = initTracker({
  debug: !isProduction,
  // The tracker's cookies (cd_cid, cd_sid, cd_perm) are statistics cookies.
  hasConsent: () => isCaptureAllowed(isStatisticsConsentGranted()),
  trackEvent: async (payload, { logger }) => {
    if (skipTracking()) {
      logger.log("Skipped event with payload: ", payload);
      return;
    } else {
      const dataLayer = window.dataLayer ?? [];
      dataLayer.push({ ecommerce: null });
      dataLayer.push(payload);
      if (payload?.event !== "page_view" && payload?.event !== "click") {
        // PostHog has its own pageview and click tracking, so we only send other events to PostHog.
        capturePostHogEvent(payload);
      }
      logger.log(`[Tracker] Pushed event: "${payload.event}"`, payload);
    }
  },
});

export async function trackEvent(
  event: Promise<TrackingEvent> | TrackingEvent,
): Promise<void> {
  try {
    const eventData = event instanceof Promise ? await event : event;
    await tracker.trackEvent(eventData);
  } catch (e) {
    Logger.warn(`Failed tracking event! \n${e}`);
  }
}
